import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, SimpleGrid, useToast, Flex, Button } from '@chakra-ui/react';
import DynamicTable from '../../../components/table/DynamicTable.js';
import { columnsDataRevalidations } from '../../../variables/columnsDataRevalidations.js';
import { getData, deleteData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import CreateRevalidationModal from '../../../components/revalidation/CreateRevalidationModal.js';
import EditRevalidationModal from '../../../components/revalidation/EditRevalidationModal.js';
import ConfirmModal from '../../../components/modal/ConfirmModal.js';

export default function Revalidations() {
  const { searchQuery } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    createModal: false,
    editModal: false,
    confirmDelete: false,
  });
  const [selectedRevalidation, setSelectedRevalidation] = useState(null);

  const rowsPerPage = 10;
  const toast = useToast();

  const latestRequestRef = useRef(0);

  const getRevalidations = useCallback(async (searchData) => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await getData(
        `/revalidations?page=${page + 1}&limit=${rowsPerPage}&data=${searchData}`,
      );
      if (requestId !== latestRequestRef.current) return;
      setData(response?.payload?.revalidations || []);
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      console.error('Error fetching revalidations:', error);
      toast({
        title: 'Error al obtener reválidas',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [page, rowsPerPage, toast]);

  const handleCreateRevalidation = async () => {
    await getRevalidations(''); // Recargar la lista
    toast({
      title: 'Reválida creada exitosamente',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleEditRevalidation = async () => {
    await getRevalidations(''); // Recargar la lista
    toast({
      title: 'Reválida actualizada exitosamente',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDeleteRevalidation = async () => {
    try {
      await deleteData(`/revalidations/${selectedRevalidation.id}`);
      await getRevalidations(''); // Recargar la lista
      toast({
        title: 'Reválida eliminada exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting revalidation:', error);
      toast({
        title: 'Error al eliminar reválida',
        description: error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getRevalidations(searchQuery);
  }, [page, searchQuery, getRevalidations]);

  const openCreateModal = () => {
    setModalState(prev => ({ ...prev, createModal: true }));
  };

  const closeCreateModal = () => {
    setModalState(prev => ({ ...prev, createModal: false }));
  };

  const openEditModal = (revalidation) => {
    setSelectedRevalidation(revalidation);
    setModalState(prev => ({ ...prev, editModal: true }));
  };

  const closeEditModal = () => {
    setModalState(prev => ({ ...prev, editModal: false }));
    setSelectedRevalidation(null);
  };

  const openDeleteModal = (revalidation) => {
    setSelectedRevalidation(revalidation);
    setModalState(prev => ({ ...prev, confirmDelete: true }));
  };

  const closeDeleteModal = () => {
    setModalState(prev => ({ ...prev, confirmDelete: false }));
    setSelectedRevalidation(null);
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="flex-end" mb={4}>
        <Button 
          colorScheme="blue" 
          onClick={openCreateModal}
          size="lg"
          fontWeight="bold"
        >
          Nueva Reválida
        </Button>
      </Flex>
      
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <DynamicTable
          columnsData={columnsDataRevalidations}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          showRowInfo={false}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />
      </SimpleGrid>

      {/* Modal para crear nueva reválida */}
      <CreateRevalidationModal
        isOpen={modalState.createModal}
        onClose={closeCreateModal}
        onSuccess={handleCreateRevalidation}
      />

      {/* Modal para editar reválida */}
      <EditRevalidationModal
        isOpen={modalState.editModal}
        onClose={closeEditModal}
        revalidation={selectedRevalidation}
        onSuccess={handleEditRevalidation}
      />

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={modalState.confirmDelete}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteRevalidation}
        header="Confirmar Eliminación"
        message={`¿Estás seguro de que deseas eliminar la reválida de ${selectedRevalidation?.student?.person?.name || 'este estudiante'} para la materia ${selectedRevalidation?.subject?.description || 'esta materia'}?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </Box>
  );
}
